import { ME_TOKEN, type FilterCondition, type FilterFieldType } from './filter';

/**
 * Canonical filter-operator catalog, one entry per `FilterFieldType`.
 *
 * THE single source of truth (schemas-as-source-of-truth) for "which
 * operators are valid for which field type" — the Filters popover's operator
 * dropdown (`web/app-home/src/components/flow/filters/FilterPopover.tsx`)
 * and the write-time saved-view validator
 * (`filter-fields.ts`'s `validateViewFieldReferences`) both consume this
 * module instead of each declaring their own list. Two independent lists is
 * exactly the drift that let a saved view persist the operator
 * `'greater_than'` (not a real operator for ANY field type) — `conditionToSql`
 * silently DROPPED the condition at query time instead of 400ing at write
 * time (R1 — the bug is a "hidden filter": the view 201s and looks saved,
 * but renders as if that condition were never added).
 *
 * Every entry below is real: read
 * `src/services/flow/query/condition-to-sql.ts` (`buildPredicate`) before
 * changing this file. Its per-type `switch` statements — plus the universal
 * `is_empty`/`is_not_empty` short-circuit that runs before any type check —
 * are the ONLY source of truth for what actually evaluates to a SQL
 * predicate. Adding an operator here that `conditionToSql` cannot evaluate
 * for that type reintroduces the exact silently-dropped-filter bug this
 * table exists to prevent.
 *
 * ONE deliberate narrowing, noted here because it looks like a mismatch
 * otherwise: `type: 'boolean'` doesn't actually gate on the operator name at
 * all in `conditionToSql` — `buildPredicate` builds
 * `COALESCE(col, false) = value` for ANY operator that isn't
 * `is_empty`/`is_not_empty`, so at the SQL layer literally any operator
 * string "works" for a boolean field. This table still restricts boolean to
 * `equals` (+ the universal empty checks) on purpose: persisting an operator
 * whose LABEL ("contains", "before", …) doesn't match the predicate it
 * actually produces would be a different, subtler silent-mismatch bug than
 * the one R1 fixes, so validation is intentionally stricter than the runtime
 * floor here.
 */
export interface FilterOperatorOption {
  value: string;
  label: string;
}

export const FILTER_OPERATORS_BY_TYPE: Record<FilterFieldType, FilterOperatorOption[]> = {
  text: [
    { value: 'equals', label: 'is' },
    { value: 'not_equals', label: 'is not' },
    { value: 'contains', label: 'contains' },
    { value: 'not_contains', label: 'does not contain' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
    // Appended, not prepended (U8) — see the picklist entry's comment below.
    { value: 'in_any_of', label: 'is any of' },
    // Appended directly after in_any_of, never prepended (QA round 7, U8,
    // item 14) — same reason: `firstOperatorFor` takes `[0]`, so neither
    // array operator must ever become the field-switch default.
    { value: 'not_in_any_of', label: 'is not any of' },
  ],
  number: [
    { value: 'equals', label: '=' },
    { value: 'not_equals', label: '≠' },
    { value: 'gt', label: '>' },
    { value: 'gte', label: '≥' },
    { value: 'lt', label: '<' },
    { value: 'lte', label: '≤' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  picklist: [
    { value: 'equals', label: 'is' },
    { value: 'not_equals', label: 'is not' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
    // Appended, not prepended (U8) — `firstOperatorFor` in FilterPopover.tsx
    // takes `[0]` as the default operator on field switch; `in_any_of` must
    // never become that default.
    { value: 'in_any_of', label: 'is any of' },
    // Appended directly after in_any_of (QA round 7, U8, item 14) — same
    // firstOperatorFor concern as above.
    { value: 'not_in_any_of', label: 'is not any of' },
    // Appended last (Signals tab PR 1, D-L) — promoted out of the now-
    // deleted `NON_UI_OPERATORS_BY_TYPE` map (see `operatorSetFor`'s doc
    // comment below for that map's history). The My/
    // All Signals presets' "Step doesn't contain auto" condition
    // (`signal-view.ts`'s `STEP_NOT_AUTO`) needed this to be write-time
    // valid before a rep could ever hand-build the same filter; a picklist
    // field's `<select>` correctly skips this operator (SUBSTRING_OPERATORS
    // in FilterPopover.tsx) and falls to the free-text branch instead —
    // `contains`/`not_contains` need a partial match, which a closed
    // `<select>` cannot express.
    { value: 'not_contains', label: 'does not contain' },
  ],
  boolean: [
    { value: 'equals', label: 'is' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  date: [
    { value: 'equals', label: 'is' },
    { value: 'gt', label: 'after' },
    { value: 'lt', label: 'before' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
    // Appended, not prepended (see the picklist entry's comment above) —
    // `firstOperatorFor` in FilterPopover.tsx takes `[0]` as the default
    // operator on field switch, so none of these must ever become it.
    // Promoted out of NON_UI_OPERATORS_BY_TYPE below (in_last_days) plus its
    // 3 new siblings, and a new fixed-range `between`.
    { value: 'in_last_days', label: 'within the last' },
    { value: 'not_in_last_days', label: 'not within the last' },
    { value: 'in_next_days', label: 'within the next' },
    { value: 'not_in_next_days', label: 'not within the next' },
    { value: 'between', label: 'is between' },
    // Signals V3 PR 1, U6 — hour-granular rolling window ("surfaced in the
    // last 24 hours"). Appended like its siblings (never the field-switch
    // default). Only meaningful on a column with a clock: a field whose
    // catalog entry is `granularity: 'day'` (a Postgres DATE) is never offered
    // these (`FilterPopover`) and `validateViewFieldReferences` rejects them.
    // Labels carry "(hours)" so the menu never shows two identical
    // "within the last" entries beside the day operators.
    { value: 'in_last_hours', label: 'within the last (hours)' },
    { value: 'not_in_last_hours', label: 'not within the last (hours)' },
    // Signals tab PR 1 (D-L) — promoted out of the now-deleted
    // `NON_UI_OPERATORS_BY_TYPE` map (see `operatorSetFor`'s doc comment
    // below for that map's history): the Overdue preset's "Due date before
    // today" (`due_date < CURRENT_DATE`, computed at query time so it never
    // goes stale, `signal-view.ts`'s `SIGNAL_PRESETS`). Joins
    // `NO_VALUE_OPERATORS` below (no value to carry) and
    // `condition-to-sql.ts` already evaluates it.
    { value: 'before_today', label: 'is before today' },
    // Phase 4 — promoted out of the (now-deleted) NON_UI_OPERATORS_BY_TYPE: the Plays default
    // Start Date chip (`buildStartDateRecentChip`, plays-view.ts) compiles
    // to `gte`, and a controlled <select> with no matching <option> silently
    // DISPLAYED "is" for it (and rewrote the operator on any touch). No
    // `lte` counterpart: `conditionToSql`'s date switch has no `lte` case,
    // so a label without compiler support would silently drop the filter.
    // Kept LAST (appended after before_today) — the field-switch default
    // (`firstOperatorFor` takes `[0]`) is `equals`, so relative order among
    // these appended entries has no default-selection consequence; this
    // ordering is preserved only to avoid re-pinning the existing "gte is
    // last" test.
    { value: 'gte', label: 'is on or after' },
  ],
  person: [
    { value: 'equals', label: 'is' },
    { value: 'not_equals', label: 'is not' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
    // Appended, not prepended (U8 follow-up) — see the picklist entry's
    // comment above; `firstOperatorFor` in FilterPopover.tsx takes `[0]` as
    // the default operator on field switch, so `in_any_of` must never become
    // it. A concrete (non-`{me}`) person value is stored as a literal name
    // string, exactly like text/picklist — `buildPredicate`'s `in_any_of`
    // case (condition-to-sql.ts) already shares that branch across
    // `text | picklist | person`. Safety against a `{me}` token landing
    // inside the array is `validateOperatorValueShape`'s guard below, not
    // this list.
    { value: 'in_any_of', label: 'is any of' },
    // Appended directly after in_any_of (QA round 7, U8, item 14) — same
    // `{me}`-safety story: the guard below rejects `{me}` inside a
    // `not_in_any_of` array too (ARRAY_VALUE_OPERATORS covers both).
    { value: 'not_in_any_of', label: 'is not any of' },
  ],
};

/**
 * The operators whose value is a string ARRAY (chips / multi-select) — one
 * set, read by `FilterPopover` (which control to render), `validateOperatorValueShape`
 * (the non-empty-array + `{me}`-safety rules) and the R6 closed-enum
 * membership check below, so the two array operators (`in_any_of` /
 * `not_in_any_of`) cannot drift apart across those three call sites.
 */
export const ARRAY_VALUE_OPERATORS: ReadonlySet<string> = new Set(['in_any_of', 'not_in_any_of']);

/**
 * Historical note — this module used to keep a second map,
 * `NON_UI_OPERATORS_BY_TYPE`: operators `conditionToSql` could evaluate for
 * a type but that had no `FilterPopover` dropdown control yet, so
 * write-time validation had to accept them independently of
 * `FILTER_OPERATORS_BY_TYPE`. Every entry that ever lived there eventually
 * graduated into `FILTER_OPERATORS_BY_TYPE` once its UI control landed
 * (`in_last_days` + 3 siblings + `between`, `gte`, `person`'s `in_any_of`,
 * and — Signals tab PR 1, D-L — `before_today` (date) and `not_contains`
 * (picklist)) — a two-map design that offered a home for "valid but no UI
 * yet" but, in practice, only ever grew a graduation queue nobody kept
 * short: R18 caught FilterPopover's own LOCAL `NO_VALUE_OPERATORS` copy
 * drifting out of sync with `before_today` living in the other map. Rather
 * than add a 4th place `before_today`/`not_contains` had to be kept in sync
 * (this map, `NO_VALUE_OPERATORS`, `FilterPopover`'s dropdown, and
 * `condition-to-sql.ts`), the map itself is deleted: `isValidOperatorForType`
 * now reads `FILTER_OPERATORS_BY_TYPE` alone.
 */
function operatorSetFor(type: FilterFieldType): ReadonlySet<string> {
  return new Set<string>(FILTER_OPERATORS_BY_TYPE[type].map(o => o.value));
}

const VALID_OPERATOR_SETS: Record<FilterFieldType, ReadonlySet<string>> = {
  text: operatorSetFor('text'),
  number: operatorSetFor('number'),
  picklist: operatorSetFor('picklist'),
  boolean: operatorSetFor('boolean'),
  date: operatorSetFor('date'),
  person: operatorSetFor('person'),
};

/** True when `operator` is one `conditionToSql` can evaluate for `type`. */
export function isValidOperatorForType(type: FilterFieldType, operator: string): boolean {
  return VALID_OPERATOR_SETS[type]?.has(operator) ?? false;
}

/**
 * The 4 rolling-window date operators (`between` is excluded — it's a single
 * string compare, not a set-membership check). Exported so `FilterPopover.tsx`
 * consumes this ONE list for its number-input branch and its
 * `coerceValueForOperatorChange` shape logic, instead of hand-copying the 4
 * names — a second list here is exactly the two-lists drift this module's
 * header comment warns about.
 */
export const RELATIVE_DATE_OPERATORS: ReadonlySet<string> = new Set([
  'in_last_days',
  'not_in_last_days',
  'in_next_days',
  'not_in_next_days',
]);

/**
 * Hour-granular rolling windows (Signals V3 PR 1, U6) — whole hours only.
 * Separate from `RELATIVE_DATE_OPERATORS` (days) because the unit, the cap
 * and the set of columns they apply to (a clock, never a calendar DATE) all
 * differ; `RELATIVE_WINDOW_OPERATORS` below is the union for the callers that
 * only care "this takes a positive count" (the popover's number input).
 */
export const RELATIVE_HOUR_OPERATORS: ReadonlySet<string> = new Set(['in_last_hours', 'not_in_last_hours']);

/** Every operator whose value is a positive whole count of a unit (days or hours). */
export const RELATIVE_WINDOW_OPERATORS: ReadonlySet<string> = new Set([...RELATIVE_DATE_OPERATORS, ...RELATIVE_HOUR_OPERATORS]);

/**
 * Operators that never need a `value` — mirrors `conditionToSql`'s universal
 * `is_empty`/`is_not_empty` short-circuit, which runs before any type or
 * value check (so it applies identically across every field type).
 *
 * `before_today` (date, OQ1 above) joins this set for the same reason:
 * "before today" has no value to carry — it compares against
 * `CURRENT_DATE` at query time — so it must skip `DATE_VALUE_OPERATORS`'s
 * "value must be a valid date" check below exactly like `is_empty` does.
 */
export const NO_VALUE_OPERATORS: ReadonlySet<string> = new Set(['is_empty', 'is_not_empty', 'before_today']);

/** Operators whose value must be numeric-coercible (`Number(value)` not NaN). */
const NUMERIC_OPERATORS: ReadonlySet<string> = new Set(['gt', 'gte', 'lt', 'lte']);

/** Operators whose value must be a valid `YYYY-MM-DD` calendar date. */
const DATE_VALUE_OPERATORS: ReadonlySet<string> = new Set(['equals', 'gt', 'lt', 'gte']);

/** F5 — upper bound on a relative-date day count: 100 years. Without this,
 * a value like `2147483648` (int32 overflow) or an absurd multi-century
 * count passes the "positive whole number" check below and is then handed
 * to Postgres, which (depending on the exact query shape) can error or
 * silently produce a nonsense window — a different flavor of the same
 * hidden-filter/500 failure mode this module exists to prevent. */
const MAX_RELATIVE_DATE_DAYS = 36500;

/** Upper bound on an hour window: 30 days. Past that, days is the right unit. */
const MAX_RELATIVE_HOURS = 720;

/**
 * F4 — strict `YYYY-MM-DD` calendar-date check. `Date.parse`/`new Date(s)`
 * are too lenient for this purpose: `Date.parse('2026-02-30')` succeeds in
 * V8/Bun (it rolls forward to March 2), but Postgres rejects
 * `'2026-02-30'::date` outright — an API-only 500 that a local dev testing
 * with valid dates would never surface. This first rejects any non-
 * `YYYY-MM-DD` shape via the regex (catching `'02/30/2026'`, `'2026-2-30'`,
 * free text, etc.), then round-trips the string through `Date` to catch a
 * semantically-invalid-but-regex-shaped date like `'2026-02-30'` — `Date`
 * normalizes Feb 30 to Mar 2, so the round-tripped ISO date string won't
 * match the original `s`. An impossible month/day (`'2026-13-01'`) yields an
 * Invalid Date whose `toISOString()` throws; this is called from request
 * parsing and render paths, so it must return false, never throw.
 */
function isValidCalendarDate(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const t = new Date(`${s}T00:00:00Z`).getTime();
  return Number.isFinite(t) && new Date(t).toISOString().slice(0, 10) === s;
}

/**
 * F5b (R1 item 4) — cheap value-shape validation, paired with `type` and
 * `operator`. Returns an error message, or null when the value is fine (or
 * when this operator/type combination has no extra shape rule beyond
 * "operator is valid for this type", which `isValidOperatorForType` already
 * checked).
 *
 * `values` (R6, optional 4th param, additive) — a catalog entry's enumerated
 * `values` (`FilterFieldCatalogEntry.values`, e.g. the tracker's Status
 * buckets) drives a closed `<select>` in `FilterPopover`, but until R6
 * nothing enforced that a WRITTEN value is actually a member. An
 * out-of-enum value (a stale bucket label predating a rename, or a direct
 * API write) used to pass validation, then render as a blank "Select…" in
 * the UI while STILL silently filtering rows by that stale value
 * server-side. When `values` is provided: `equals`/`not_equals` check the
 * stringified value is a member, and `in_any_of` checks every array element
 * is. Omitting `values` (every existing call site, and every field without
 * an enumerated list) is a no-op — this never changes behavior for a field
 * that doesn't declare `values`.
 */
export function validateOperatorValueShape(
  type: FilterFieldType,
  operator: string,
  value: FilterCondition['value'],
  values?: readonly string[],
): string | null {
  if (NO_VALUE_OPERATORS.has(operator)) return null;

  // person in_any_of/not_in_any_of — the {me} array guard (write-time
  // backstop). `{me}` resolves per-viewer via a value-equality check in
  // resolveMeTokens (isMeCondition: `cond.value === ME_TOKEN`, exact string
  // match) that an array never satisfies, so a raw '{me}' inside either
  // array operator's value would slip past that rewrite and be compared as
  // a literal name — a hidden filter that matches nothing (or, for
  // not_in_any_of, everything but that literal string). Runs
  // unconditionally, independent of the empty-array check below.
  if (ARRAY_VALUE_OPERATORS.has(operator) && type === 'person' && Array.isArray(value) && value.includes(ME_TOKEN)) {
    return `value must not include the "{me}" token in "${operator}" — {me} resolves per-viewer and has no meaning inside a literal-name array`;
  }

  // U8 — `value: []` for in_any_of/not_in_any_of is not "no filter", it's a
  // hidden, silently-wrong filter: an empty `= ANY(...)` matches nothing
  // (in_any_of), and its SQL-level negation would otherwise be the accident
  // of "matches everything" rather than a documented contract (not_in_any_of
  // — see MATCH_EVERYTHING in condition-to-sql.ts). Left unvalidated, this
  // persists a filter whose behavior nobody chose — a view that 201s, looks
  // saved, and quietly returns the wrong rows, exactly the class of
  // hidden-filter bug this module exists to prevent (see the module doc
  // comment). This check runs independently of (and before) the R6
  // membership check below.
  if (ARRAY_VALUE_OPERATORS.has(operator) && !(Array.isArray(value) && value.length > 0)) {
    return `value must be a non-empty array for operator "${operator}"`;
  }

  // R5 — relative-date operators: value must be a positive whole number of
  // days. Pre-fix, `value: 'abc'`/`0`/negative all passed validation and
  // then silently vanished at query time (`conditionToSql` returns null for
  // them) — the exact hidden-filter failure mode this module exists to
  // prevent.
  if (type === 'date' && RELATIVE_DATE_OPERATORS.has(operator)) {
    if (value == null || Array.isArray(value) || !Number.isInteger(Number(value)) || Number(value) <= 0) {
      return `value must be a positive whole number of days for operator "${operator}"`;
    }
    if (Number(value) > MAX_RELATIVE_DATE_DAYS) {
      return `value must be at most ${MAX_RELATIVE_DATE_DAYS} days (100 years) for operator "${operator}"`;
    }
  }

  // Signals V3 PR 1, U6 — hour windows: a positive whole number of hours,
  // 1..720. Same hidden-filter reasoning as the day rule above.
  if (type === 'date' && RELATIVE_HOUR_OPERATORS.has(operator)) {
    if (value == null || Array.isArray(value) || !Number.isInteger(Number(value)) || Number(value) <= 0) {
      return `value must be a positive whole number of hours for operator "${operator}"`;
    }
    if (Number(value) > MAX_RELATIVE_HOURS) {
      return `value must be at most ${MAX_RELATIVE_HOURS} hours (30 days) for operator "${operator}"`;
    }
  }

  // R5 — between: 2-element array of parseable dates, first <= second.
  // Validate-and-error (not auto-swap) per the plan's recommended default —
  // matches every other rule in this function, which returns a message
  // rather than silently mutating the value.
  if (type === 'date' && operator === 'between') {
    if (!Array.isArray(value) || value.length !== 2) {
      return 'value must be a 2-element array of dates for operator "between"';
    }
    const [start, end] = value;
    if (!isValidCalendarDate(String(start)) || !isValidCalendarDate(String(end))) {
      return 'value must be a 2-element array of valid dates for operator "between"';
    }
    // Both are now confirmed valid YYYY-MM-DD strings, so lexicographic
    // comparison is equivalent to chronological comparison.
    if (String(start) > String(end)) {
      return `the first date ("${String(start)}") must not be after the second date ("${String(end)}") for operator "between"`;
    }
  }

  if (type === 'number' && NUMERIC_OPERATORS.has(operator)) {
    if (value == null || Array.isArray(value) || Number.isNaN(Number(value))) {
      return `value must be numeric for operator "${operator}"`;
    }
  }

  if (type === 'date' && DATE_VALUE_OPERATORS.has(operator)) {
    // Narrowed to `string | number` here (null/array already excluded), so
    // `String(value)` is always a safe, meaningful stringification — never
    // the '[object Object]' base-to-string footgun.
    if (value == null || Array.isArray(value) || !isValidCalendarDate(String(value))) {
      return `value must be a valid date for operator "${operator}"`;
    }
  }

  if (values) {
    if (operator === 'equals' || operator === 'not_equals') {
      if (value != null && !values.includes(String(value))) {
        return `value "${String(value)}" is not one of the allowed values for this field`;
      }
    } else if (ARRAY_VALUE_OPERATORS.has(operator) && Array.isArray(value)) {
      const invalid = value.find(v => !values.includes(String(v)));
      if (invalid !== undefined) {
        return `value "${String(invalid)}" is not one of the allowed values for this field`;
      }
    }
  }

  return null;
}
